import React, { Component } from 'react';
import { arrayOf, bool, object, func, shape, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Switch, Route, withRouter } from 'react-router-dom';

import { useRouteConfiguration } from '../context/routeConfigurationContext';
import { propTypes } from '../util/types';
import * as log from '../util/log';
import { canonicalRoutePath } from '../util/routes';
import { useConfiguration } from '../context/configurationContext';

import { locationChanged } from '../ducks/routing.duck';

import { NamedRedirect } from '../components';
import NotFoundPage from '../containers/NotFoundPage/NotFoundPage';

import LoadableComponentErrorBoundary from './LoadableComponentErrorBoundary/LoadableComponentErrorBoundary';
import { saveScrollPosition, restoreScrollPosition, getRestorationKey } from '../util/scrollRestoration';

const canShowComponent = props => {
  const { isAuthenticated, route } = props;
  const { auth } = route;
  return !auth || isAuthenticated;
};

const callLoadData = props => {
  const { match, location, route, dispatch, logoutInProgress, config } = props;
  const { loadData, name } = route;
  const shouldLoadData =
    typeof loadData === 'function' && canShowComponent(props) && !logoutInProgress;

  if (shouldLoadData) {
    dispatch(loadData(match.params, location.search, config))
      .then(() => {
        if (props.logLoadDataCalls) {
          // This gives good input for debugging issues on live environments, but with test it's not needed.
          console.log(`loadData success for ${name} route`);
        }
      })
      .catch(e => {
        log.error(e, 'load-data-failed', { routeName: name });
      });
  }
};

const setPageScrollPosition = (location, delayed, prevLocation, historyAction) => {
  // Skip scroll-to-top if only query params changed (pathname is the same)
  // This prevents unwanted scrolling when filters are applied via URL query params
  if (prevLocation && prevLocation.pathname === location.pathname && !location.hash) {
    // Only query params changed (no hash), don't scroll to top
    // Let the component handle its own scroll behavior (e.g., scroll to search section)
    return;
  }

  // Browser back/forward: restore where the user was on this page instead of
  // forcing scroll-to-top. Falls back to doing nothing if nothing was saved.
  if (historyAction === 'POP') {
    restoreScrollPosition(getRestorationKey(location));
    return;
  }

  if (!location.hash) {
    // No hash, scroll to top (only if pathname changed)
    window.scroll({
      top: 0,
      left: 0,
    });
  } else {
    const el = document.querySelector(location.hash);
    if (el) {
      // Found element from the current page with the given fragment identifier,
      // scrolling to that element.
      //
      // NOTE: This only works on in-app navigation within the same page.
      // If smooth scrolling is needed between different pages, one needs to wait
      //   1. loadData fetch and
      //   2. code-chunk fetch
      // before making el.scrollIntoView call.

      el.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    } else {
      // A naive attempt to make a delayed call to scrollIntoView.
      // The element (e.g. #leave-review) may not exist yet because data is still
      // loading. We guard both the initial and the retry lookup so we never call
      // .scrollIntoView() on null and surface a "Fetching sale data failed" toast.
      // 300 ms is a best-effort heuristic; if the element still isn't present we
      // silently skip rather than throwing a TypeError.
      delayed = window.setTimeout(() => {
        const reTry = document.querySelector(location.hash);
        if (reTry) {
          reTry.scrollIntoView({
            block: 'start',
            behavior: 'smooth',
          });
        }
      }, 300);
    }
  }
};

const handleLocationChanged = (
  dispatch,
  location,
  routeConfiguration,
  delayed,
  prevLocation,
  historyAction
) => {
  setPageScrollPosition(location, delayed, prevLocation, historyAction);
  const path = canonicalRoutePath(routeConfiguration, location);
  dispatch(locationChanged(location, path));
};

/**
 * RouteComponentRenderer handles loadData calls on client-side.
 * It also checks authentication and redirects unauthenticated users
 * away from routes that are for authenticated users only
 * (aka "auth: true" is set in routeConfiguration.js)
 *
 * This component is a container: it needs to be connected to Redux.
 */
class RouteComponentRenderer extends Component {
  componentDidMount() {
    const { dispatch, location, routeConfiguration, history } = this.props;
    this.delayed = null;

    // Continuously persist scroll position (keyed by history location key) so that a
    // later browser-back to this exact entry can restore it. rAF-throttled since scroll
    // fires at high frequency.
    this._scrollRAF = null;
    this._handleScroll = () => {
      if (this._scrollRAF) return;
      this._scrollRAF = window.requestAnimationFrame(() => {
        this._scrollRAF = null;
        saveScrollPosition(
          getRestorationKey(this.props.location),
          window.scrollY ?? window.pageYOffset ?? 0
        );
      });
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', this._handleScroll, { passive: true });
    }

    // Calling loadData on initial rendering (on client side).
    callLoadData(this.props);
    handleLocationChanged(dispatch, location, routeConfiguration, this.delayed, undefined, history?.action);
  }

  componentDidUpdate(prevProps) {
    const { dispatch, location, routeConfiguration, history } = this.props;
    // Call for handleLocationChanged affects store/state
    // and it generates an unnecessary update.
    if (prevProps.location !== this.props.location) {
      // Calling loadData after initial rendering (on client side).
      // This makes it possible to use loadData as default client side data loading technique.
      // However it is better to fetch data before location change to avoid "Loading data" state.
      callLoadData(this.props);
      handleLocationChanged(
        dispatch,
        location,
        routeConfiguration,
        this.delayed,
        prevProps.location,
        history?.action
      );
    }
  }

  componentWillUnmount() {
    if (this.delayed) {
      window.clearTimeout(this.resetTimeoutId);
    }
    if (typeof window !== 'undefined' && this._handleScroll) {
      window.removeEventListener('scroll', this._handleScroll);
    }
    if (this._scrollRAF) {
      window.cancelAnimationFrame(this._scrollRAF);
    }
  }

  render() {
   
    const { route, match, location, staticContext } = this.props;
    const { component: RouteComponent, authPage = 'SignupPage', extraProps } = route;
    const canShow = canShowComponent(this.props);
    
    if (!canShow) {
      staticContext.unauthorized = true;
    }
    return canShow ? (
      <LoadableComponentErrorBoundary staticContext={staticContext}>
        <RouteComponent params={match.params} location={location} {...extraProps} />
      </LoadableComponentErrorBoundary>
    ) : (
      <NamedRedirect
        name={authPage}
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }
}

RouteComponentRenderer.defaultProps = { staticContext: {} };

RouteComponentRenderer.propTypes = {
  isAuthenticated: bool.isRequired,
  logoutInProgress: bool.isRequired,
  route: propTypes.route.isRequired,
  routeConfiguration: arrayOf(propTypes.route).isRequired,
  match: shape({
    params: object.isRequired,
    url: string.isRequired,
  }).isRequired,
  location: shape({
    search: string.isRequired,
  }).isRequired,
  history: object,
  staticContext: object,
  dispatch: func.isRequired,
};

const mapStateToProps = state => {
  const { isAuthenticated, logoutInProgress } = state.auth;
  return { isAuthenticated, logoutInProgress };
};
const RouteComponentContainer = compose(connect(mapStateToProps))(RouteComponentRenderer);

/**
 * Routes component creates React Router rendering setup.
 * It needs routeConfiguration (named as "routes") through props.
 * Using that configuration it creates navigation on top of page-level
 * components. Essentially, it's something like:
 * <Switch>
 *   <Route render={pageA} />
 *   <Route render={pageB} />
 * </Switch>
 */
const Routes = (props, context) => {
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const { isAuthenticated, logoutInProgress, logLoadDataCalls } = props;

  const toRouteComponent = route => {
    const renderProps = {
      isAuthenticated,
      logoutInProgress,
      route,
      routeConfiguration,
      config,
      logLoadDataCalls,
    };
    

    // By default, our routes are exact.
    // https://reacttraining.com/react-router/web/api/Route/exact-bool
    const isExact = route.exact != null ? route.exact : true;
    return (
      <Route
        key={route.name}
        path={route.path}
        exact={isExact}
        render={matchProps => (
          <RouteComponentContainer
            {...renderProps}
            match={matchProps.match}
            location={matchProps.location}
            history={matchProps.history}
            staticContext={matchProps.staticContext}
          />
        )}
      />
    );
  };

  return (
    <Switch>
      {routeConfiguration.map(toRouteComponent)}
      <Route component={NotFoundPage} />
    </Switch>
  );
};

export default withRouter(Routes);
